import { createIdentifier, type FrameworkProvider } from '@toeverything/infra';

import { AccessibleDocCacheStore } from '../../doc/stores/accessible-doc-cache';
import { AccessibleFolderCacheStore } from '../../organize/stores/accessible-folder-cache';

/** Read access to cached pages and folders for permission events. */
export interface PermissionResourceReader {
  /**
   * Ids of the given folders and all their descendants, or null when one of
   * them is not cached.
   */
  getFolderSubtreeIds(folderIds: readonly string[]): string[] | null;
  /** Ids of cached pages whose parent folder is one of `folderIds`. */
  getDocIdsInFolders(folderIds: ReadonlySet<string>): string[];
}

export const PermissionResourceReader =
  createIdentifier<PermissionResourceReader>('PermissionResourceReader');

/**
 * Reads the workspace caches on demand. Both caches depend on the permission
 * store, so they are resolved per call instead of at construction.
 */
export function createPermissionResourceReader(
  provider: FrameworkProvider
): PermissionResourceReader {
  return {
    getFolderSubtreeIds(folderIds) {
      const folders = provider
        .get(AccessibleFolderCacheStore)
        .getCachedFolders();
      const known = new Set(folders.map(folder => folder.id));
      if (folderIds.some(folderId => !known.has(folderId))) return null;
      const childrenOf = new Map<string, string[]>();
      for (const folder of folders) {
        if (!folder.parentId) continue;
        childrenOf.set(folder.parentId, [
          ...(childrenOf.get(folder.parentId) ?? []),
          folder.id,
        ]);
      }
      const subtree = new Set<string>();
      const stack = [...folderIds];
      while (stack.length > 0) {
        const folderId = stack.pop()!;
        if (subtree.has(folderId)) continue;
        subtree.add(folderId);
        stack.push(...(childrenOf.get(folderId) ?? []));
      }
      return [...subtree];
    },
    getDocIdsInFolders(folderIds) {
      return provider
        .get(AccessibleDocCacheStore)
        .getCachedDocs()
        .filter(doc => !!doc.parentId && folderIds.has(doc.parentId))
        .map(doc => doc.id);
    },
  };
}
